import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Globe, MapPin } from "lucide-react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";

export default function AdminDestinations() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, object = form
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/destinations/admin/all");
      setList(r.data);
    } catch { toast.error("Failed to load destinations"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async (dest) => {
    try {
      await adminApi.put(`/destinations/${dest.id}`, { published: !dest.published });
      setList((l) => l.map((d) => (d.id === dest.id ? { ...d, published: !d.published } : d)));
      toast.success(dest.published ? "Unpublished" : "Published");
    } catch { toast.error("Failed to update"); }
  };

  const remove = async (dest) => {
    if (!window.confirm(`Delete "${dest.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/destinations/${dest.id}`);
      setList((l) => l.filter((d) => d.id !== dest.id));
      toast.success("Destination deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const startNew = () => setEditing({
    name: "", slug: "", region: "India", category: "inbound", tagline: "", short: "",
    price_from: "", nights: "", hero: "", overview: "", best_time: "", travel_tips: "",
    highlights: "", gallery: "", published: true, sort_order: 0,
  });

  const startEdit = (dest) => setEditing({
    ...dest,
    highlights: (dest.highlights || []).join("\n"),
    gallery: (dest.gallery || []).join("\n"),
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...editing,
        highlights: editing.highlights ? editing.highlights.split("\n").map((s) => s.trim()).filter(Boolean) : [],
        gallery: editing.gallery ? editing.gallery.split("\n").map((s) => s.trim()).filter(Boolean) : [],
      };
      if (editing.id) {
        await adminApi.put(`/destinations/${editing.id}`, payload);
        toast.success("Destination saved");
      } else {
        await adminApi.post("/destinations", payload);
        toast.success("Destination created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));
  const inputCls = "w-full bg-transparent border border-border focus:border-accent transition-colors px-4 py-3 outline-none text-sm";
  const labelCls = "eyebrow block mb-2";

  if (editing) {
    return (
      <form onSubmit={save} className="space-y-8 max-w-5xl" data-testid="admin-destination-form">
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => setEditing(null)} className="text-[11px] uppercase tracking-[0.25em] text-foreground/60 hover:text-accent">
            ← Back to destinations
          </button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors uppercase text-[11px] tracking-[0.25em] disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl">{editing.id ? editing.name : "New destination."}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Name</label><input required value={editing.name} onChange={set("name")} className={inputCls} /></div>
              <div><label className={labelCls}>Slug</label><input value={editing.slug} onChange={set("slug")} className={inputCls} placeholder="auto from name" /></div>
            </div>
            <div><label className={labelCls}>Tagline</label><input value={editing.tagline} onChange={set("tagline")} className={inputCls} /></div>
            <div><label className={labelCls}>Short description</label><textarea rows={2} value={editing.short} onChange={set("short")} className={`${inputCls} resize-none`} /></div>
            <div><label className={labelCls}>Overview</label><textarea rows={5} value={editing.overview} onChange={set("overview")} className={`${inputCls} resize-y`} /></div>
            <div><label className={labelCls}>Highlights (one per line)</label><textarea rows={6} value={editing.highlights} onChange={set("highlights")} className={`${inputCls} resize-y`} /></div>
            <div><label className={labelCls}>Travel tips</label><textarea rows={4} value={editing.travel_tips || ""} onChange={set("travel_tips")} className={`${inputCls} resize-y`} /></div>
          </div>
          <div className="space-y-6">
            <div>
              <label className={labelCls}>Region</label>
              <select value={editing.region} onChange={set("region")} className={inputCls}>
                <option value="India">India</option>
                <option value="International">International</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select value={editing.category} onChange={set("category")} className={inputCls}>
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </select>
            </div>
            <div><label className={labelCls}>Price from</label><input value={editing.price_from} onChange={set("price_from")} className={inputCls} placeholder="$8,500" /></div>
            <div><label className={labelCls}>Nights</label><input value={editing.nights} onChange={set("nights")} className={inputCls} placeholder="10–12" /></div>
            <div><label className={labelCls}>Best time to visit</label><input value={editing.best_time} onChange={set("best_time")} className={inputCls} /></div>
            <div><label className={labelCls}>Hero image URL</label><input value={editing.hero} onChange={set("hero")} className={inputCls} /></div>
            {editing.hero && <img src={editing.hero} alt="" className="w-full aspect-video object-cover border border-border" />}
            <div><label className={labelCls}>Gallery URLs (one per line)</label><textarea rows={4} value={editing.gallery} onChange={set("gallery")} className={`${inputCls} resize-y font-mono text-xs`} /></div>
            <div><label className={labelCls}>Sort order</label><input type="number" value={editing.sort_order} onChange={set("sort_order")} className={inputCls} /></div>
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={!!editing.published} onChange={set("published")} className="accent-current" />
              <span>Published</span>
            </label>
          </div>
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-destinations-list">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-3">Content</div>
          <h1 className="font-serif text-4xl md:text-5xl">Destinations.</h1>
        </div>
        <button
          onClick={startNew}
          className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors uppercase text-[11px] tracking-[0.25em]"
        >
          <Plus size={14} strokeWidth={1.5} /> New Destination
        </button>
      </div>

      {loading ? (
        <div className="text-foreground/50 text-center py-20">Loading…</div>
      ) : list.length === 0 ? (
        <div className="border border-border p-16 text-center text-foreground/60">No destinations yet.</div>
      ) : (
        <ul className="space-y-3">
          {list.map((d) => (
            <li key={d.id} className="border border-border bg-background flex flex-col md:flex-row md:items-center gap-4 p-4 md:p-5">
              <div className="w-full md:w-24 aspect-[4/3] md:aspect-square overflow-hidden shrink-0 bg-secondary">
                {d.hero && <img src={d.hero} alt={d.name} className="h-full w-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="eyebrow text-foreground/55">{d.region}</span>
                  {d.category === "inbound" ? <MapPin size={12} className="text-accent" /> : <Globe size={12} className="text-accent" />}
                  {!d.published && <span className="text-[9px] uppercase tracking-[0.22em] border border-border px-2 py-0.5 text-foreground/55">Draft</span>}
                </div>
                <div className="font-serif text-xl md:text-2xl leading-tight">{d.name}</div>
                <div className="text-[11px] text-foreground/55 mt-1">{d.tagline} · {d.nights} nights · From {d.price_from}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => togglePublish(d)} title={d.published ? "Unpublish" : "Publish"} className="p-2.5 border border-border hover:border-foreground transition-colors">
                  {d.published ? <Eye size={14} strokeWidth={1.5} /> : <EyeOff size={14} strokeWidth={1.5} />}
                </button>
                <button onClick={() => startEdit(d)} title="Edit" className="p-2.5 border border-border hover:border-foreground transition-colors">
                  <Pencil size={14} strokeWidth={1.5} />
                </button>
                <button onClick={() => remove(d)} title="Delete" className="p-2.5 border border-border hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
